import type { FailedProvider, LeakageBasis, LeakageNullReason, RevenueLeakage } from '@/api/revenueLeakage'
import {
  Card,
  EmptyState,
  formatCurrency,
  formatInteger,
  formatPercent,
  HorizontalBarChart,
  KpiCard,
  LoadingBlock,
  percentChange,
  type FormatOptions,
} from '@/design'
import type { GlobalFilters } from '@/lib/filters'

import { useRevenueLeakage } from './queries'
import { RefundTimeline } from './RefundTimeline'
import { failureKindLabel, LEAKAGE_NULL_TEXT, providerLabel, QUESTIONS, refundReasonLabel } from './wording'
import '../charts.css'
import './leakage.css'

/** What the page's shell passes each tab: its filters, its basis and how money reads (`slots.tsx`'s `RevenueSlotProps`). */
export interface LeakageTabProps {
  filters: GlobalFilters
  basis: LeakageBasis
  money: FormatOptions
}

/**
 * The Revenue page's Leakage tab (`GET /analytics/revenue/leakage`): where money went back
 * (refunds by reason, and by the day they were issued with draw days shaded), what was
 * given away (discounts) and what the gateway cost (failed payments, and how much came
 * back within 24 hours). Every null says why in words — a failed-payment panel with no
 * events from the website says so, never £0.
 */
export function LeakageTab({ filters, basis, money }: LeakageTabProps) {
  const leakage = useRevenueLeakage(filters, basis)
  if (leakage.isPending) {
    return (
      <Card>
        <LoadingBlock rows={6} label="Loading refunds, discounts and failed payments" />
      </Card>
    )
  }
  if (!leakage.data) {
    return (
      <Card>
        <EmptyState variant="outage" announce description="Refunds, discounts and failed payments could not be loaded just now. Try again in a moment." />
      </Card>
    )
  }
  const { data, meta } = leakage.data
  const options: FormatOptions = { ...money, currency: data.currency }
  const compareLabel = meta.period?.compareLabel ?? null
  return (
    <div className="rl-leakage" aria-busy={leakage.isFetching || undefined}>
      <LeakageKpis data={data} money={options} compareLabel={compareLabel} />
      <div className="rl-panels">
        <RefundReasons data={data} money={options} />
        <Discounts data={data} money={options} />
        <FailedPayments data={data} money={options} />
      </div>
      <Card title="Refunds by day issued" subtitle={QUESTIONS.timeline}>
        {data.refunds.count === 0 ? (
          <EmptyState variant="no-results" compact title="No refunds in this period" description="Nothing was refunded on any day of the period." />
        ) : (
          <RefundTimeline days={data.refunds.byDay} draws={data.draws} money={options} />
        )}
      </Card>
    </div>
  )
}

/** The API's median lag (two places) as it reads: "3.25 days", "1 day". */
function lagDays(days: number, money: FormatOptions): string {
  const shown = new Intl.NumberFormat(money.locale ?? 'en-GB', { maximumFractionDigits: 2 }).format(days)
  return `${shown} ${days === 1 ? 'day' : 'days'}`
}

/** A share, or why there is none: "1.9% of gross", "No revenue in the period". */
const shareOf = (value: number | null, reason: LeakageNullReason | null, of: string) =>
  value === null ? (reason ? LEAKAGE_NULL_TEXT[reason] : '—') : `${formatPercent(value)} of ${of}`

function LeakageKpis({ data, money, compareLabel }: { data: RevenueLeakage; money: FormatOptions; compareLabel: string | null }) {
  const { refunds, discounts, failedPayments: failed } = data
  const amount = (value: number) => formatCurrency(value, money)
  const notCompared = data.previousUnavailable === 'not_compared' ? 'No comparison' : 'No comparison for this period'
  return (
    <section aria-labelledby="rl-kpis-heading">
      <h2 id="rl-kpis-heading" className="ci-visually-hidden">
        Leakage in the period
      </h2>
      <ul className="rl-kpis">
        <li>
          <KpiCard
            label="Refunded"
            value={amount(refunds.amount)}
            change={percentChange(refunds.amount, refunds.previousAmount)}
            polarity="down_good"
            comparisonLabel={compareLabel}
            note={notCompared}
            detail={`${shareOf(refunds.rate, refunds.rateNullReason, 'gross')} · ${formatInteger(refunds.count, money)} refunds`}
          />
        </li>
        <li>
          <KpiCard
            label="Median refund lag"
            value={refunds.medianLagDays === null ? '—' : lagDays(refunds.medianLagDays, money)}
            change={null}
            polarity="neutral"
            note={refunds.medianLagNullReason ? LEAKAGE_NULL_TEXT[refunds.medianLagNullReason] : 'From completed order to refund'}
          />
        </li>
        <li>
          <KpiCard
            label="Discounts"
            value={amount(discounts.amount)}
            change={percentChange(discounts.amount, discounts.previousAmount)}
            polarity="down_good"
            comparisonLabel={compareLabel}
            note={notCompared}
            detail={shareOf(discounts.shareOfGross, discounts.shareNullReason, 'gross')}
          />
        </li>
        <li>
          <KpiCard
            label="Failed payment value"
            value={failed ? amount(failed.value) : '—'}
            change={null}
            polarity="down_good"
            note={failed ? `${formatInteger(failed.attempts, money)} failed attempts` : LEAKAGE_NULL_TEXT[data.failedPaymentsNullReason ?? 'needs_payment_events']}
            detail={failed ? shareOf(failed.shareOfGross, failed.shareNullReason, 'gross') : undefined}
          />
        </li>
        <li>
          <KpiCard
            label="Recovered within 24 h"
            value={failed ? amount(failed.recoveredValue) : '—'}
            change={null}
            polarity="up_good"
            note={failed ? shareOf(failed.recoveredRate, failed.recoveredRateNullReason, 'failed value') : LEAKAGE_NULL_TEXT[data.failedPaymentsNullReason ?? 'needs_payment_events']}
          />
        </li>
      </ul>
    </section>
  )
}

function RefundReasons({ data, money }: { data: RevenueLeakage; money: FormatOptions }) {
  const { refunds } = data
  return (
    <Card title="Refunds by reason" subtitle={QUESTIONS.refunds} className="rl-panel">
      {refunds.byReason.length === 0 ? (
        <EmptyState variant="no-results" compact title="No refunds in this period" description="Nothing was refunded in the period." />
      ) : (
        <HorizontalBarChart
          title="Refunded, by the reason the website recorded"
          format="currency"
          formatOptions={money}
          rows={refunds.byReason.map((row) => ({
            key: row.reason,
            label: refundReasonLabel(row.reason),
            value: row.amount,
            detail: `${formatInteger(row.count, money)} ${row.count === 1 ? 'refund' : 'refunds'}`,
          }))}
        />
      )}
    </Card>
  )
}

function Discounts({ data, money }: { data: RevenueLeakage; money: FormatOptions }) {
  const { discounts } = data
  return (
    <Card title="Discounts by coupon" subtitle={QUESTIONS.discounts} className="rl-panel">
      <p className="rl-panel__figure">
        <strong>{formatCurrency(discounts.amount, money)}</strong> given away on {formatInteger(discounts.ordersWithDiscount, money)} discounted{' '}
        {discounts.ordersWithDiscount === 1 ? 'order' : 'orders'}
      </p>
      <EmptyState variant="not-live" compact title="Not by coupon yet" description={`${LEAKAGE_NULL_TEXT[discounts.byCouponNullReason]}: the website does not send which coupon an order used, so discounts cannot be split by coupon or campaign.`} />
    </Card>
  )
}

/** A provider's row: "Cashflows · Declined", its failed value, attempts and what came back. */
function providerRow(row: FailedProvider, money: FormatOptions) {
  return {
    key: `${row.provider}:${row.failureKind}`,
    label: `${providerLabel(row.provider)} · ${failureKindLabel(row.failureKind)}`,
    value: row.value,
    detail: `${formatInteger(row.attempts, money)} ${row.attempts === 1 ? 'attempt' : 'attempts'} · ${formatCurrency(row.recoveredValue, money)} recovered`,
  }
}

function FailedPayments({ data, money }: { data: RevenueLeakage; money: FormatOptions }) {
  const failed = data.failedPayments
  return (
    <Card title="Failed payments by provider" subtitle={QUESTIONS.failed} className="rl-panel">
      {!failed ? (
        <EmptyState
          variant="no-data"
          compact
          title="No failed-payment events yet"
          description="The website has not sent a failed payment in this period or the 90 days before it. Failed payments appear once the connector sends its payment events: they are not counted as zero."
        />
      ) : failed.byProvider.length === 0 ? (
        <EmptyState variant="no-results" compact title="No failed payments in this period" description="Every payment the website reported in the period went through." />
      ) : (
        <>
          <p className="rl-panel__figure">
            <strong>{formatCurrency(failed.unrecoveredValue, money)}</strong> not recovered within 24 hours, of {formatCurrency(failed.value, money)} failed
            {failed.attemptsWithoutValue > 0 ? ` (${formatInteger(failed.attemptsWithoutValue, money)} attempts sent no value)` : ''}
          </p>
          <HorizontalBarChart
            title="Failed value, by payment provider and why it failed"
            format="currency"
            formatOptions={money}
            rows={failed.byProvider.map((row) => providerRow(row, money))}
          />
        </>
      )}
    </Card>
  )
}
